import {
    DatabaseOutlined,
    ExclamationCircleOutlined,
    SaveOutlined,
    SettingOutlined,
} from '@ant-design/icons';
import {
    Alert,
    Button,
    Divider,
    Form,
    Input,
    Modal,
    Space,
    Tabs,
    Typography,
    Select
} from 'antd';
import React, { useEffect, useState } from 'react';
import {
    clearDatabaseData,
    getActiveEnvironment,
    getAllEnvironments,
    saveEnvironment,
    validateEnvironment,
} from '../../services/environmentService';
import { syncAll } from '../../services/syncService';
import db from '../../db/database';
import { COUNTRIES } from "../../constants/countries.js";
import { useConfig } from "../../contexts/ConfigContext";

const { Title, Text } = Typography;
const { TabPane } = Tabs;

const EnvironmentConfigModal = ({ visible, onClose, onEnvironmentChanged }) => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [clearingDatabase, setClearingDatabase] = useState(false);
    const [syncing, setSyncing] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [currentEnvironment, setCurrentEnvironment] = useState(null);
    const [environments, setEnvironments] = useState([]);
    const [showConfirmation, setShowConfirmation] = useState(false);
    const [pendingValues, setPendingValues] = useState(null);
    const { setActiveEnvironment } = useConfig();

    useEffect(() => {
        const loadData = () => {
            if (!visible) return;

            loadCurrentEnvironment();
            loadAllEnvironments();
        }

        loadData();
    }, [visible]);

    const loadCurrentEnvironment = async () => {
        try {
            const env = await getActiveEnvironment();
            setCurrentEnvironment(env);
            form.setFieldsValue({
                company: env.company,
                env: env.env,
                apiUrl: env.apiUrl,
                bcBaseUrl: env.bcBaseUrl,
                country: env.country
            });
        } catch (error) {
            console.error('Error cargando entorno actual:', error);
        }
    };

    const loadAllEnvironments = async () => {
        try {
            const envList = await getAllEnvironments();
            setEnvironments(envList);
        } catch (error) {
            console.error('Error cargando entornos:', error);
        }
    };

    const handleSubmit = async (values) => {
        try {
            setError('');
            setSuccess('');
            setLoading(true);

            // Validar configuración
            validateEnvironment(values);

            // Verificar si hay cambios significativos
            const hasSignificantChanges = currentEnvironment && (
                currentEnvironment.company !== values.company ||
                currentEnvironment.env !== values.env ||
                currentEnvironment.apiUrl !== values.apiUrl ||
                currentEnvironment.country !== values.country
            );

            if (hasSignificantChanges) {
                // Mostrar confirmación para cambios significativos
                setPendingValues(values);
                setShowConfirmation(true);
                setLoading(false); // Resetear loading mientras esperamos confirmación
            } else {
                // Guardar sin limpiar la base de datos
                console.log('Guardando configuración sin cambios significativos...');
                const result = await saveEnvironment(values);
                console.log('Configuración guardada:', result);
                setSuccess('Configuración guardada correctamente');
                if (onEnvironmentChanged) {
                    onEnvironmentChanged();
                }
            }
        } catch (error) {
            console.error('Error en handleSubmit:', error);
            setError(`Error: ${error.message}`);
        } finally {
            setLoading(false);
        }
    };

    const saveNewEnvironmentAndSync = async (values) => {
        try {
            setClearingDatabase(true);
            setSuccess('Limpiando base de datos...');

            // Limpiar base de datos
            await clearDatabaseData();

            // Guardar nueva configuración
            const savedEnv = await saveEnvironment(values);

            setSuccess('Base de datos limpiada. Iniciando sincronización...');
            setSyncing(true);

            // Sincronizar con el nuevo entorno
            const syncResult = await syncAll();

            if (syncResult.success) {
                setSuccess('Configuración guardada y sincronización completada');
            } else {
                setError(`Configuración guardada pero error en sincronización: ${syncResult.message}`);
            }

            // Actualizar contexto con el nuevo entorno activo
            if (savedEnv?.id) {
                await setActiveEnvironment(savedEnv.id);
            }

            if (onEnvironmentChanged) {
                onEnvironmentChanged();
            }

            // Cerrar modal después de un momento
            setTimeout(() => {
                onClose();
            }, 2000);

        } catch (error) {
            setError(`Error durante el proceso: ${error.message}`);
        } finally {
            setClearingDatabase(false);
            setSyncing(false);
        }
    };

    const loadEnvironmentPreset = (env) => {
        form.setFieldsValue({
            company: env.company,
            env: env.env,
            apiUrl: env.apiUrl,
            country: env.country,
        });
    };

    const isProcessing = loading || clearingDatabase || syncing;

    // Función de prueba para diagnosticar problemas
    const testDatabaseConnection = async () => {
        try {
            console.log('Probando conexión a la base de datos...');

            // Probar acceso básico a la tabla
            const count = await db.environment.count();
            console.log('Número de entornos en la base de datos:', count);

            // Probar obtener todos los registros
            const allEnvs = await db.environment.toArray();
            console.log('Todos los entornos:', allEnvs);

            // Probar añadir un registro de prueba
            const testEnv = {
                company: 'Test Company',
                env: 'Test_Env',
                apiUrl: 'https://test.com',
                country: 'EC',
                isActive: false,
                lastModified: new Date(),
            };

            const testId = await db.environment.add(testEnv);
            console.log('Registro de prueba añadido con ID:', testId);

            // Eliminar el registro de prueba
            await db.environment.delete(testId);
            console.log('Registro de prueba eliminado');

            setSuccess('Prueba de base de datos exitosa. Revisa la consola para más detalles.');
        } catch (error) {
            console.error('Error en prueba de base de datos:', error);
            setError(`Error en prueba de BD: ${error.message}`);
        }
    };

    // Confirmar cambios significativos
    const handleConfirmChanges = async () => {
        try {
            console.log('Usuario confirmó cambios, ejecutando saveNewEnvironmentAndSync');
            setShowConfirmation(false);
            setLoading(true);
            await saveNewEnvironmentAndSync(pendingValues);
            setPendingValues(null);
        } catch (error) {
            console.error('Error al confirmar cambios:', error);
            setError(`Error: ${error.message}`);
            setLoading(false);
        }
    };

    // Cancelar cambios
    const handleCancelChanges = () => {
        console.log('Usuario canceló cambios');
        setShowConfirmation(false);
        setPendingValues(null);
        setLoading(false);
    };

    return (
        <Modal
            title={
                <Space>
                    <SettingOutlined />
                    Configuración del Entorno
                </Space>
            }
            visible={visible}
            onCancel={onClose}
            footer={null}
            width={800}
            maskClosable={!isProcessing}
            closable={!isProcessing}
        >
            <Tabs defaultActiveKey="config">
                <TabPane tab="Configuración" key="config">
                    {error && (
                        <Alert
                            message={error}
                            type="error"
                            showIcon
                            style={{ marginBottom: 16 }}
                        />
                    )}

                    {success && (
                        <Alert
                            message={success}
                            type="success"
                            showIcon
                            style={{ marginBottom: 16 }}
                        />
                    )}

                    {showConfirmation && (
                        <Alert
                            message="¿Confirmar cambio de entorno?"
                            description={
                                <div>
                                    <p>Al cambiar la configuración del entorno se borrará toda la base de datos local, incluyendo:</p>
                                    <ul>
                                        <li>Clientes</li>
                                        <li>Productos</li>
                                        <li>Facturas</li>
                                        <li>Usuarios</li>
                                        <li>Turnos, cajas y todos los demás datos</li>
                                    </ul>
                                    <p><strong>Esta acción no se puede deshacer.</strong></p>
                                    <div style={{ marginTop: 16 }}>
                                        <Space>
                                            <Button
                                                type="primary"
                                                danger
                                                onClick={handleConfirmChanges}
                                                loading={loading}
                                            >
                                                Sí, cambiar y sincronizar
                                            </Button>
                                            <Button onClick={handleCancelChanges}>
                                                Cancelar
                                            </Button>
                                        </Space>
                                    </div>
                                </div>
                            }
                            type="warning"
                            showIcon
                            icon={<ExclamationCircleOutlined />}
                            style={{ marginBottom: 16 }}
                        />
                    )}

                    <Form
                        form={form}
                        layout="vertical"
                        onFinish={handleSubmit}
                        disabled={isProcessing || showConfirmation}
                        onFinishFailed={(errorInfo) => {
                            console.error('Error en validación del formulario:', errorInfo);
                            setError('Error en la validación del formulario. Revisa los campos.');
                        }}
                    >
                        <Title level={5}>Business Central</Title>

                        <Form.Item
                            name="company"
                            label="Empresa"
                            rules={[{ required: true, message: 'La empresa es obligatoria' }]}
                        >
                            <Input placeholder="Nombre de la empresa en Business Central" disabled />
                        </Form.Item>

                        <Form.Item
                            name="env"
                            label="Entorno"
                            rules={[{ required: true, message: 'El entorno es obligatorio' }]}
                        >
                            <Input placeholder="Nombre del entorno" disabled />
                        </Form.Item>

                        <Form.Item
                            name="country"
                            label="País"
                            rules={[
                                {
                                    required: true,
                                    message: "Por favor seleccione el país",
                                },
                            ]}
                        >
                            <Select
                                placeholder="Seleccione un país"
                                showSearch
                                optionFilterProp="children"
                                filterOption={(input, option) =>
                                    (option?.label || "").toLowerCase().includes(input.toLowerCase())
                                }
                                disabled
                            >
                                {COUNTRIES.map((country) => (
                                    <Select.Option
                                        key={country.code}
                                        value={country.code}
                                        label={country.name}
                                    >
                                        {country.name}
                                    </Select.Option>
                                ))}
                            </Select>
                        </Form.Item>

                        <Form.Item
                            name="apiUrl"
                            label="URL de la API"
                            rules={[
                                { required: true, message: 'La URL de la API es obligatoria' },
                                { type: 'url', message: 'Debe ser una URL válida' }
                            ]}
                        >
                            <Input placeholder="https://ecuadordev-api.exceliaone.com" disabled />
                        </Form.Item>

                        <Divider />

                        <Form.Item>
                            <Space>
                                <Button
                                    type="primary"
                                    htmlType="submit"
                                    icon={<SaveOutlined />}
                                    loading={isProcessing}
                                    disabled={showConfirmation}
                                >
                                    {clearingDatabase ? 'Limpiando base de datos...' :
                                        syncing ? 'Sincronizando...' :
                                            loading ? 'Guardando...' : 'Guardar Configuración'}
                                </Button>
                                <Button
                                    type="default"
                                    onClick={testDatabaseConnection}
                                    disabled={isProcessing || showConfirmation}
                                >
                                    Probar BD
                                </Button>
                                <Button onClick={onClose} disabled={isProcessing || showConfirmation}>
                                    Cancelar
                                </Button>
                            </Space>
                        </Form.Item>
                    </Form>
                </TabPane>

                <TabPane tab="Entornos Guardados" key="saved">
                    <div>
                        <Title level={5}>Configuraciones Guardadas</Title>
                        <Text type="secondary">
                            Haga clic en una configuración para cargarla en el formulario
                        </Text>

                        {environments.length === 0 ? (
                            <Alert
                                message="No hay configuraciones guardadas"
                                type="info"
                                style={{ marginTop: 16 }}
                            />
                        ) : (
                            <div style={{ marginTop: 16 }}>
                                {environments.map((env) => (
                                    <div
                                        key={env.id}
                                        style={{
                                            border: '1px solid #d9d9d9',
                                            borderRadius: 6,
                                            padding: 12,
                                            marginBottom: 8,
                                            cursor: 'pointer',
                                            backgroundColor: env.isActive ? '#f6ffed' : '#fafafa',
                                            borderColor: env.isActive ? '#b7eb8f' : '#d9d9d9',
                                        }}
                                        onClick={() => loadEnvironmentPreset(env)}
                                    >
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <div>
                                                <Text strong>{env.company}</Text>
                                                {env.isActive && <Text type="success"> (Activo)</Text>}
                                                <br />
                                                <Text type="secondary" style={{ fontSize: '12px' }}>
                                                    {env.env} - {new Date(env.lastModified).toLocaleString()}
                                                </Text>
                                            </div>
                                            <DatabaseOutlined style={{ fontSize: 16, color: '#1890ff' }} />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </TabPane>
            </Tabs>
        </Modal>
    );
};

export default EnvironmentConfigModal; 